import type { SupabaseClient } from '@supabase/supabase-js';
import { geofipsVariantsForQuery } from '../energyConsumptionSources';
import {
  resolveSolidWasteYearValue,
  solidWasteChartValue,
  SOLID_WASTE_FACTOR_CAP_YEAR,
  type SolidWasteYearResult,
} from './solidWasteCore';

const INVENTORY_YEARS = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);

export interface SolidWasteSectorResult {
  byYear: Map<number, SolidWasteYearResult>;
  chartSeries: { year: number; category: string; emissions: number | null }[];
  factorVintageYear: number;
}

async function resolveCountyGeofips(
  supabase: SupabaseClient,
  geofips: number,
  geographyType?: string | null,
  countyName?: string | null
): Promise<{ countyGeofips: number; resolved: boolean }> {
  if (geographyType === 'County' || geographyType === 'county') {
    return { countyGeofips: geofips, resolved: true };
  }
  if (!countyName) return { countyGeofips: geofips, resolved: false };

  const { data: countyData } = await supabase
    .from('population')
    .select('geofips')
    .eq('location_name', countyName)
    .eq('geography_type', 'County')
    .limit(1);

  if (countyData?.[0]?.geofips != null) {
    return { countyGeofips: Number(countyData[0].geofips), resolved: true };
  }
  return { countyGeofips: geofips, resolved: false };
}

async function fetchWasteTonsByYear(
  supabase: SupabaseClient,
  geofips: number
): Promise<Record<number, number>> {
  const variants = geofipsVariantsForQuery(geofips);
  const { data: wasteData } = await supabase
    .from('total_waste')
    .select('year, tons_waste, geofips')
    .in('geofips', variants)
    .order('year', { ascending: true });

  const wasteTonsByYear: Record<number, number> = {};
  (wasteData || []).forEach((r: { year: number; tons_waste?: number }) => {
    const year = r.year;
    wasteTonsByYear[year] = (wasteTonsByYear[year] || 0) + Number(r.tons_waste || 0);
  });
  return wasteTonsByYear;
}

async function buildEmissionFactorsByYear(
  supabase: SupabaseClient,
  countyGeofips: number
): Promise<Record<number, number>> {
  const countyVariants = geofipsVariantsForQuery(countyGeofips);
  const { data: landfillData } = await supabase
    .from('landfill_emissions_full')
    .select('year, waste_tons, ghg_emissions')
    .in('geofips', countyVariants)
    .order('year', { ascending: true });

  const countyTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
  (landfillData || []).forEach((r: { year: number; waste_tons?: number; ghg_emissions?: number }) => {
    const year = r.year;
    if (!countyTotalsByYear[year]) countyTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
    countyTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
    countyTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
  });

  const emissionFactorsByYear: Record<number, number> = {};
  Object.keys(countyTotalsByYear).forEach((yearStr) => {
    const year = parseInt(yearStr, 10);
    const totals = countyTotalsByYear[year];
    if (totals.totalWaste > 0) {
      emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
    }
  });

  const hasValid =
    Object.keys(emissionFactorsByYear).length > 0 &&
    Object.values(emissionFactorsByYear).some((f) => f > 0);

  if (!hasValid) {
    const { data: californiaLandfillData } = await supabase
      .from('landfill_emissions_full')
      .select('year, waste_tons, ghg_emissions, geofips')
      .gte('geofips', 6000)
      .lt('geofips', 7000)
      .order('year', { ascending: true });

    const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
    (californiaLandfillData || []).forEach(
      (r: { year: number; waste_tons?: number; ghg_emissions?: number }) => {
        const year = r.year;
        if (!stateTotalsByYear[year]) stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
        stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
        stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
      }
    );

    Object.keys(stateTotalsByYear).forEach((yearStr) => {
      const year = parseInt(yearStr, 10);
      const totals = stateTotalsByYear[year];
      if (totals.totalWaste > 0) {
        emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
      }
    });
  }

  const factorYears = Object.keys(emissionFactorsByYear)
    .map((y) => parseInt(y, 10))
    .sort((a, b) => a - b);
  if (factorYears.length > 0) {
    const earliest = Math.min(...factorYears);
    const base = emissionFactorsByYear[earliest];
    for (let year = 2005; year < earliest; year++) {
      if (!emissionFactorsByYear[year]) emissionFactorsByYear[year] = base;
    }
    const latest = Math.max(...factorYears);
    if (latest < 2023 && factorYears.length >= 2) {
      const secondLast = factorYears[factorYears.length - 2];
      const lastFactor = emissionFactorsByYear[latest];
      const secondFactor = emissionFactorsByYear[secondLast];
      const trend = (lastFactor - secondFactor) / (latest - secondLast);
      for (let year = latest + 1; year <= 2023; year++) {
        emissionFactorsByYear[year] = lastFactor + trend * (year - latest);
      }
    } else if (latest < 2023) {
      for (let year = latest + 1; year <= 2023; year++) {
        emissionFactorsByYear[year] = emissionFactorsByYear[latest];
      }
    }
  }

  return emissionFactorsByYear;
}

async function backcastWasteTons(
  supabase: SupabaseClient,
  geofips: number,
  wasteTonsByYear: Record<number, number>
): Promise<void> {
  const variants = geofipsVariantsForQuery(geofips);
  const { data: populationData } = await supabase
    .from('population')
    .select('year, population')
    .in('geofips', variants)
    .order('year', { ascending: true });

  const populationByYear: Record<number, number> = {};
  (populationData || []).forEach((r: { year: number; population?: number }) => {
    populationByYear[r.year] = Number(r.population || 0);
  });

  const wasteYears = Object.keys(wasteTonsByYear)
    .map((y) => parseInt(y, 10))
    .sort((a, b) => a - b);
  const earliest = wasteYears[0];
  if (
    earliest &&
    earliest > 2005 &&
    populationByYear[earliest] &&
    populationByYear[earliest] > 0
  ) {
    const baseTons = wasteTonsByYear[earliest];
    const basePop = populationByYear[earliest];
    for (let year = 2005; year < earliest; year++) {
      const yearPop = populationByYear[year] || basePop;
      wasteTonsByYear[year] = baseTons * (yearPop / basePop);
    }
  }
}

export async function fetchSolidWasteSectorValues(
  supabase: SupabaseClient,
  geofips: number
): Promise<SolidWasteSectorResult> {
  const byYear = new Map<number, SolidWasteYearResult>();

  const { data: locationData } = await supabase
    .from('population')
    .select('geography_type, county')
    .in('geofips', geofipsVariantsForQuery(geofips))
    .limit(1);

  const geo = locationData?.[0];
  const { countyGeofips } = await resolveCountyGeofips(
    supabase,
    geofips,
    geo?.geography_type,
    geo?.county
  );

  const wasteTonsByYear = await fetchWasteTonsByYear(supabase, geofips);
  await backcastWasteTons(supabase, geofips, wasteTonsByYear);
  const emissionFactorsByYear = await buildEmissionFactorsByYear(supabase, countyGeofips);

  for (const year of INVENTORY_YEARS) {
    const wasteTons = wasteTonsByYear[year] || 0;
    const rawFactor = emissionFactorsByYear[year];
    const factor =
      rawFactor != null && rawFactor > 0
        ? rawFactor
        : year > SOLID_WASTE_FACTOR_CAP_YEAR && emissionFactorsByYear[SOLID_WASTE_FACTOR_CAP_YEAR]
          ? emissionFactorsByYear[SOLID_WASTE_FACTOR_CAP_YEAR]
          : null;
    const resolved = resolveSolidWasteYearValue(year, wasteTons, factor, SOLID_WASTE_FACTOR_CAP_YEAR);
    byYear.set(year, resolved);
  }

  const chartSeries = INVENTORY_YEARS.map((year) => ({
    year,
    category: 'Waste',
    emissions: solidWasteChartValue(byYear.get(year)!),
  }));

  return {
    byYear,
    chartSeries,
    factorVintageYear: SOLID_WASTE_FACTOR_CAP_YEAR,
  };
}
